<template>
  <div class="sign-pdf-editor" id="signatureContainer">
    <div class="sign-pdf-content" id="signPdfContent" :style="{ width: `${containerWidth}px` }">
      <VueDragResize
        v-for="sealDragItem in sealDragList"
        :key="sealDragItem.id"
        :style="{ zIndex: 100, cursor: 'pointer' }"
        :x="sealDragItem.x"
        :y="sealDragItem.y"
        :w="sealDragItem.w"
        :h="sealDragItem.h"
        :parentW="containerWidth"
        :parentH="parentHeight"
        parentLimitation
        :isActive="sealDragItem.isDraggable"
        :isDraggable="sealDragItem.isDraggable"
        :isResizable="false"
        @dragstop="info => sealDragStopHandle(sealDragItem, info)"
      >
        <i
          class="el-icon-error seal-del-icon"
          v-if="sealDragItem.couldDel"
          @click="$emit('delSeal', sealDragItem.sealItem)"
        ></i>
        <seal
          :sealType="sealDragItem.sealType"
          :isPreview="sealDragItem.isPreview"
          :isArea="sealDragItem.isArea"
          :scale="scale"
          :imgUrl="sealDragItem.sealImgUrl"
          :personName="sealDragItem.personName"
          :dateText="sealDragItem.dateText"
        />
      </VueDragResize>
      <div id="pdf-el">
        <div id="viewer" class="pdfViewer"></div>
      </div>
    </div>
  </div>
</template>

<script>
import seal from './seal';
import VueDragResize from 'vue-drag-resize';

const PDFJS = require('pdfjs-dist');
PDFJS.GlobalWorkerOptions.workerSrc = `${location.origin}/contract/pdf.worker.js`;
import 'pdfjs-dist/web/pdf_viewer.css';
import * as pdfjsViewer from 'pdfjs-dist/web/pdf_viewer';

import { debounce, uniqueId } from 'lodash';
import { sealInfoEnum } from '@/constant';

export default {
  components: { seal, VueDragResize },
  data() {
    return {
      pdfInfo: null,
      pdfPageCount: 0,
      containerWidth: 0,
      pdfPageHeight: 0,
      pdfPageWidth: 0,
      scale: 1,
      sealDragList: [],
      resizeObserver: null
    };
  },
  props: {
    pdfFile: {
      type: ArrayBuffer,
      required: true
    },
    sealList: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    pdfFile() {
      this.destroyPdfHandle();
      this.pdfInfo = null;
      this.pdfPageWidth = 0;
      this.pdfPageHeight = 0;
      this.refreshPdfHandle();
    },
    sealList() {
      this.initSealDragList();
    }
  },
  computed: {
    paddingWidth() {
      return 40;
    },
    maxScale() {
      return 2;
    },
    pagePixelX() {
      return this.pdfPageWidth / this.scale / 100;
    },
    pagePixelY() {
      return this.pdfPageHeight / this.scale / 100;
    },
    parentHeight() {
      if (!this.pdfPageCount || !this.pdfPageHeight) {
        return 0;
      }
      const marginHeight = this.pdfPageCount * 10 + 7;
      const pdfHeight = this.pdfPageHeight * this.pdfPageCount;

      return marginHeight + pdfHeight;
    }
  },
  beforeDestroy() {
    this.destroyPdfHandle();
    this.resizeObserver.disconnect();
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(() => {
      this.refreshPdfHandle();
    });
    this.resizeObserver.observe(document.getElementById('signatureContainer'));
    this.refreshPdfHandle = debounce(this.refreshPdfHandle.bind(this), 500, { leading: false, trailing: true });
  },
  methods: {
    destroyPdfHandle() {
      if (this.pdfInfo) {
        this.pdfInfo.loadingTask.destroy();
      }
    },
    async refreshPdfHandle() {
      try {
        this.sealDragList = [];
        let loadingTask = this.pdfInfo ? this.pdfInfo.loadingTask : null;
        if (!loadingTask) {
          loadingTask = PDFJS.getDocument(this.pdfFile);
        }
        const pdfDocument = await loadingTask.promise;
        this.pdfPageCount = pdfDocument.numPages;
        await this.initPdfContainerWidth(pdfDocument);
        await this.$nextTick();

        const eventBus = new pdfjsViewer.EventBus();

        const pdfViewer = new pdfjsViewer.PDFViewer({
          container: document.getElementById('pdf-el'),
          eventBus,
          textLayerMode: 0
        });
        eventBus.on('pagesinit', () => {
          pdfViewer.currentScaleValue = 'page-width';
        });
        pdfViewer.setDocument(pdfDocument);
        this.pdfInfo = { loadingTask };
        this.initSealDragList();
        this.$emit('inited', { pageCount: this.pdfPageCount });
      } catch (err) {
        this.$error(err);
      }
    },
    async initPdfContainerWidth(pdfDocument) {
      const page = await pdfDocument.getPage(1);
      const viewport = page.getViewport({ scale: 1 });
      const maxWidth = viewport.width * this.maxScale;
      const signatureContainer = document.getElementById('signatureContainer');
      const { width } = signatureContainer.getBoundingClientRect();

      let effWidth = width - this.paddingWidth;
      if (effWidth > maxWidth) {
        effWidth = maxWidth;
      }
      if (effWidth < 400) {
        effWidth = 400;
      }
      this.containerWidth = effWidth + this.paddingWidth;
      this.scale = effWidth / viewport.width;
      this.pdfPageWidth = effWidth;
      this.pdfPageHeight = Math.round(viewport.height * this.scale);
    },
    initSealDragList() {
      if (!this.pdfPageWidth || !this.pdfPageHeight) {
        return;
      }
      const sealDragList = [];
      this.sealList.forEach(item => {
        const { width, height } = sealInfoEnum[item.sealType];
        const w = Math.round(width * this.scale);
        const h = Math.round(height * this.scale);
        let x = 0;
        let y = 0;
        let mockDragStop = false;
        if (item.position.needInit) {
          const signPdfContent = document.getElementById('signPdfContent');
          x = Math.round(this.containerWidth / 2 - w * 0.5);
          y = Math.round(signPdfContent.clientHeight / 2 - h * 0.5) + signPdfContent.scrollTop;
          item.position.needInit = false;
          mockDragStop = true;
        } else if (item.position.isPercent) {
          const sealPage = item.position.page || 1;
          const xRatio = item.position.xRatio || 0.5;
          const yRatio = 1 - (item.position.yRatio || 0.5);
          x = Math.round(this.pagePixelX * xRatio * 100 * this.scale) + 20.5;
          const prePdfHeight = this.getPrePdfHeight(sealPage) + (sealPage > 1 ? 11 : 10);
          y = Math.round(this.pagePixelY * yRatio * 100 * this.scale) + prePdfHeight;
        } else {
          x = Math.round(item.position.x * this.scale);
          y = Math.round(item.position.y * this.scale);
        }
        const sealDragItem = {
          sealType: item.sealType,
          sealImgUrl: item.sealImgUrl,
          isPreview: item.isPreview,
          isArea: item.isArea,
          couldDel: item.couldDel,
          personName: item.personName,
          dateText: item.dateText,
          isDraggable: item.isDraggable,
          x,
          y,
          w,
          h,
          id: `drag_${uniqueId()}`,
          sealItem: item
        };
        sealDragList.push(sealDragItem);
        if (mockDragStop) {
          this.sealDragStopHandle(sealDragItem, {
            left: x,
            top: y,
            width: w,
            height: h
          });
        }
      });
      this.sealDragList = sealDragList;
    },
    getPrePdfHeight(page) {
      // 前面pdf的高度，pdf高度+【marginTop-11(第一个的marginTop为10)】
      return (page - 1) * (this.pdfPageHeight + 11) - (page > 1 ? 1 : 0);
    },
    sealDragStopHandle(item, info) {
      const sealPage = this.getSealDragPage(info);
      const nodeItem = document.getElementsByClassName('canvasWrapper')[sealPage - 1];
      let { top: nodeTop, width: nodeWidth, height: nodeHeight } = nodeItem.getBoundingClientRect();
      const { top: parentTop } = document.getElementById('signatureContainer').getBoundingClientRect();
      nodeTop = nodeTop + document.getElementById('signPdfContent').scrollTop - parentTop;
      let xRatio = 0;
      let yRatio = 0;

      if (info.left > 20.5 && info.left + info.width < 20.5 + nodeWidth) {
        xRatio = (info.left - 20.5) / nodeWidth;
      }
      if (info.top > nodeTop && info.top + info.height < nodeTop + nodeHeight) {
        yRatio = 1 - (info.top - nodeTop) / nodeHeight;
      }

      item.sealItem.position.x = Math.round(info.left / this.scale);
      item.sealItem.position.y = Math.round(info.top / this.scale);
      item.sealItem.position.page = sealPage;
      item.sealItem.position.xRatio = xRatio;
      item.sealItem.position.yRatio = yRatio;
      this.$emit('dragStop');
    },
    getSealDragPage(info) {
      let page = 0;
      const parentScrollTop = document.getElementById('signPdfContent').scrollTop;
      const nodeArr = [...document.getElementsByClassName('canvasWrapper')];
      const { top: parentTop } = document.getElementById('signatureContainer').getBoundingClientRect();
      let nodeItem = null;
      while ((nodeItem = nodeArr.shift())) {
        const pdfTop = nodeItem.getBoundingClientRect().top + parentScrollTop - parentTop;
        if (pdfTop > info.top + info.height) {
          break;
        }
        page++;
      }

      return page || 1;
    },
    routeToPage(page) {
      const pageNode = document.getElementsByClassName('canvasWrapper')[page - 1];
      if (!pageNode) {
        return;
      }
      const signPdfContent = document.getElementById('signPdfContent');
      signPdfContent.scrollTop = pageNode.getBoundingClientRect().top + signPdfContent.scrollTop - 20;
    },
    routeToSeal(sealItem) {
      document.getElementById('signPdfContent').scrollTop = Math.round(sealItem.position.y * this.scale) - 200;
    }
  }
};
</script>

<style lang="scss" scoped>
.sign-pdf-editor {
  width: 100%;
  height: 100%;

  .sign-pdf-content {
    position: relative;
    width: 0;
    height: 100%;
    margin: 0 auto;
    overflow: auto;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .seal-del-icon {
    position: absolute;
    top: -15px;
    right: -15px;
    cursor: pointer;
    font-size: 20px;
    color: rgba(#000, 0.59);
    padding: 5px;
  }
}
</style>
